import { useCallback } from "react";
import { useWorkspace } from "@/context/WorkspaceContext";

/**
 * Resolve a company's site URL from the active workspace.
 *
 * The schedule surfaces (the scheduled-reports table, the schedule dialog) only
 * carry a company id + name, so they cannot render a favicon on their own —
 * `activeWorkspace.companies` is where `company_url` actually lives. Two callers
 * need the same lookup, hence a hook rather than a copy in each.
 *
 * Returns null when the workspace has not loaded yet or the company is not in
 * it. That is deliberately not an error: DomainFavicon falls back to the name
 * monogram on a null url, which is exactly what these rows rendered before.
 */
export function useCompanyUrl(): (companyId?: number | null) => string | null {
  const { activeWorkspace } = useWorkspace();

  return useCallback(
    (companyId?: number | null) => {
      if (companyId == null) return null;
      return (
        activeWorkspace?.companies?.find((c) => c.company_id === companyId)?.company_url ?? null
      );
    },
    [activeWorkspace],
  );
}

export default useCompanyUrl;
